import React, { useState, useEffect } from "react";
import {
  Card,
  Stack,
  Typography,
  Button,
  Box,
  TextField,
  Skeleton,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridOverlay,
} from "@mui/x-data-grid";
import TablePagination from "@mui/material/TablePagination";
import moment from "moment-timezone";

// ── Skeleton rows shown inside the DataGrid while loading ─────────────────────
function PurchaseLoadingOverlay() {
  const widths = [60, 160, 130, 180, 160, 160, 110, 100, 130];
  return (
    <GridOverlay>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 2,
          gap: 0,
        }}
      >
        {[...Array(10)].map((_, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 2,
              py: 1.2,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: i % 2 === 0 ? "background.paper" : "grey.50",
            }}
          >
            {widths.map((w, j) => (
              <Skeleton
                key={j}
                variant="rounded"
                width={w}
                height={j >= 7 ? 28 : 20}
                sx={{ flexShrink: 0, borderRadius: j >= 7 ? 1.5 : 0.5 }}
              />
            ))}
          </Box>
        ))}
      </Box>
    </GridOverlay>
  );
}

// ── Page skeleton shown before the DataGrid frame is ready ────────────────────
function PurchasePageSkeleton() {
  return (
    <Card>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="flex-end"
        alignItems="center"
        mb={3}
        px={5}
      >
        <Skeleton variant="rounded" width={250} height={36} sx={{ mt: 2.5 }} />
      </Stack>

      {/* Fake table header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 4,
          py: 1.5,
          bgcolor: "grey.100",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {[60, 160, 130, 180, 160, 160, 110, 100, 130].map((w, i) => (
          <Skeleton key={i} variant="rounded" width={w} height={18} sx={{ flexShrink: 0 }} />
        ))}
      </Box>

      {/* Fake rows */}
      {[...Array(10)].map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 4,
            py: 1.3,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: i % 2 === 0 ? "background.paper" : "grey.50",
          }}
        >
          {[60, 160, 130, 180, 160, 160, 110, 100, 130].map((w, j) => (
            <Skeleton key={j} variant="rounded" width={w} height={20} sx={{ flexShrink: 0 }} />
          ))}
        </Box>
      ))}

      {/* Fake pagination */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 4, py: 1.5 }}>
        <Skeleton variant="rounded" width={260} height={32} />
      </Box>
    </Card>
  );
}

export default function ReportCard({ purchaseDetails, loading }) {
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [purchaseDetail, setPurchaseDetail] = useState([]);

  useEffect(() => {
    if (Array.isArray(purchaseDetails)) {
      // Sort newest first
      const sorted = [...purchaseDetails].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setPurchaseDetail(sorted);
    }
  }, [purchaseDetails]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  const filteredUsers = purchaseDetail.filter((user) =>
    Object.values(user).some((value) =>
      value?.toString().toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const totalUsers = filteredUsers.length;
  const paginatedUsers = filteredUsers
    .slice(page * pageSize, page * pageSize + pageSize)
    .map((row, index) => {
      const formattedDate = row.purchased_at
        ? moment.tz(row.purchased_at, "UTC").tz("Asia/Kolkata").format("YYYY-MM-DD HH:mm:ss")
        : null;
      return {
        ...row,
        index,
        purchased_at: formattedDate,
        uniqueId: `${row.id}_${row.order_id}`,
      };
    });

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 70,
      valueGetter: ({ row }) => row.index + 1,
    },
    {
      field: "user_name",
      headerName: "Name",
      width: 180,
      valueGetter: ({ row }) => `${row.first_name} ${row.last_name}`,
    },
    {
      field: "user_phone",
      headerName: "Phone",
      width: 140,
      valueGetter: ({ row }) => row.mobile_no,
    },
    {
      field: "user_email",
      headerName: "Email",
      width: 210,
      valueGetter: ({ row }) => row.email,
    },
    {
      field: "txnId",
      headerName: "Order ID",
      width: 180,
      valueGetter: ({ row }) => {
        if (row?.assigned_by === 1) {
          return "ASSIGNED_BY_ADMIN";
        }
        if (row.order_id === "0" || row.order_id === 0 || !row.order_id) {
          return "FREE_ORDER";
        }
        return row.order_id;
      },
    },
    {
      field: "course",
      headerName: "Item Purchased",
      width: 200,
      valueGetter: ({ row }) => row?.course_name || row?.testseries_name,
    },
    {
      field: "purchaseAt",
      headerName: "Purchased At",
      width: 160,
      valueGetter: ({ row }) =>
        row?.purchased_at
          ? `${row.purchased_at.slice(0, 10)} ${row.purchased_at.slice(11, 19)}`
          : "—",
    },
    {
      field: "amount",
      type: "number",
      headerName: "Amount Paid",
      width: 120,
      valueGetter: ({ row }) => (row?.assigned_by === 1 ? 0 : row?.price),
    },
    {
      field: "couponCode",
      headerName: "Coupon",
      width: 160,
      valueGetter: ({ row }) => (row?.coupon_id ? row?.coupon : "NA"),
    },
  ];

  const CustomToolbar = () => (
    <Box sx={{ display: "flex", gap: 3, mb: 2, mt: 2 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </Box>
  );

  // Show full-page skeleton until data arrives
  if (loading || !Array.isArray(purchaseDetails)) {
    return <PurchasePageSkeleton />;
  }

  return (
    <div>
      <Card>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-end"
          alignItems={{ xs: "flex-start", sm: "center" }}
          mb={3}
          px={5}
        >
          <TextField
            label="Search by Name"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            sx={{
              width: "250px",
              marginTop: "20px",
              "& label": { color: "text.secondary" },
              "& fieldset": { borderColor: "divider" },
            }}
          />
        </Stack>

        <DataGrid
          rows={paginatedUsers}
          columns={columns}
          getRowId={(row) => row.uniqueId}
          hideFooterPagination
          page={page}
          loading={false}
          autoHeight
          slots={{ toolbar: CustomToolbar, loadingOverlay: PurchaseLoadingOverlay }}
          style={{
            marginLeft: "30px",
            marginTop: "10px",
            marginBottom: "10px",
            border: "none",
          }}
          rowCount={totalUsers}
        />

        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>
    </div>
  );
}
